import { useState } from "react";
import useFaqT from "./useFaqT";
import FaqMediaSlot from "./FaqMediaSlot";
import FaqStepList from "./FaqStepList";
import { FAQ_SHOW_KEYS } from "../../constants/faq/faqConfig";

// Answer block (design F5, capped 860px): 2–3 sentence answer, optional
// Desktop|Mobile toggle, media slot with overlay, step list. ONE component —
// everything on sheet F8 is a state of it, not a variant (design sheet note 1).
//
// Toggle rules (sheet E): rendered only when BOTH image sets exist,
// pre-selected by viewport (parent passes defaultSet), freely switchable and
// remembered per question (parent owns the map). Questions without a mobile
// set omit the control entirely.
const FaqAnswerBlock = ({ question, imgSet, onSetImgSet, labelledBy, contentId }) => {
  const tr = useFaqT();
  // Marker ↔ step coupling state, local per open answer.
  const [activeN, setActiveN] = useState(null);
  const [tappedN, setTappedN] = useState(null);

  const hasDesktop = Boolean(question.media && question.media.desktop);
  const hasMobile = Boolean(question.media && question.media.mobile);
  const hasBoth = hasDesktop && hasMobile;
  const set = hasBoth ? imgSet : hasDesktop ? "desktop" : hasMobile ? "mobile" : null;

  const effectiveN = activeN != null ? activeN : tappedN;
  const activate = (n) => setActiveN(n);
  const deactivate = () => setActiveN(null);
  const tap = (n) => setTappedN((prev) => (prev === n ? null : n));

  const segClass = (on) =>
    // 44px on touch widths (brief §5 "touch targets ≥44px") — 40px measured
    // short at 360/390 in the control pass; desktop keeps the design's compact
    // segment height.
    `border-0 px-3.5 py-2.5 text-[12px] font-medium min-h-[44px] md:min-h-0 md:py-[7px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F5EFF] ${
      on ? "bg-white text-[#14171C] shadow-[0_1px_2px_rgba(20,23,28,.12)]" : "bg-transparent text-[#5A626E]"
    }`;

  return (
    <div
      id={contentId}
      role="region"
      aria-labelledby={labelledBy}
      className="flex max-w-[860px] flex-col gap-4 px-1 pb-6 pt-1 md:gap-5 md:pb-8"
    >
      <div className="text-[14px] leading-[1.6] text-[#3A414C] md:text-[15px]">{tr(question.answer)}</div>

      {hasBoth && (
        <div className="flex items-center gap-3">
          <div className="inline-flex self-start border border-[#DDE0E5] bg-[#F5F6F8] p-[2px]">
            <button type="button" aria-pressed={set === "desktop"} onClick={() => onSetImgSet("desktop")} className={segClass(set === "desktop")}>
              {tr("Desktop")}
            </button>
            <button type="button" aria-pressed={set === "mobile"} onClick={() => onSetImgSet("mobile")} className={segClass(set === "mobile")}>
              {tr("Mobile")}
            </button>
          </div>
          {FAQ_SHOW_KEYS && (
            <span className="hidden font-faqmono text-[10px] text-[#8A919D] md:block">
              pre-selected by viewport · freely switchable
            </span>
          )}
        </div>
      )}

      {set && (
        <FaqMediaSlot
          question={question}
          set={set}
          activeN={effectiveN}
          onActivate={activate}
          onDeactivate={deactivate}
          onTap={tap}
        />
      )}

      {set && (
        <FaqStepList
          steps={question.steps}
          activeN={effectiveN}
          onActivate={activate}
          onDeactivate={deactivate}
          onTap={tap}
        />
      )}
    </div>
  );
};

export default FaqAnswerBlock;
